import { useEffect, useMemo, useRef, useState } from "react";
import { Spin, Button } from "antd";
import { DownloadOutlined } from "@ant-design/icons";

// 定义 PDF.js 的类型（简化版）
interface PDFDocumentProxy {
  numPages: number;
  getPage: (pageNumber: number) => Promise<PDFPageProxy>;
  destroy: () => void;
}

interface PDFPageProxy {
  getViewport: (params: { scale: number }) => PDFPageViewport;
  render: (params: {
    canvasContext: CanvasRenderingContext2D;
    viewport: PDFPageViewport;
  }) => {
    promise: Promise<void>;
    cancel: () => void;
  };
}

interface PDFPageViewport {
  width: number;
  height: number;
}

interface PDFViewerProps {
  file: string;
  mode?: "pagination" | "scroll" | "continuous"; // default: pagination
  onLoadSuccess?: () => void;
}

export const PDFViewer = ({
  file,
  mode = "pagination",
  onLoadSuccess,
}: PDFViewerProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [pdfDoc, setPdfDoc] = useState<PDFDocumentProxy | null>(null);
  const pdfDocRef = useRef<PDFDocumentProxy | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);
  const renderTaskRef = useRef<any>(null);
  const pdfLibRef = useRef<any>(null);
  const [pdfJsReady, setPdfJsReady] = useState(false);

  const proxyUrl = useMemo(() => {
    if (!file) return "";

    const useProxy = import.meta.env.DEV;
    const domain =
      import.meta.env.VITE_OSS_DOMAIN ||
      "https://bee-ai-test.oss-cn-shenzhen.aliyuncs.com";
    const proxyPath = import.meta.env.VITE_OSS_PROXY_PATH || "/oss-proxy";

    if (!useProxy) return file;
    if (!file.includes(domain)) return file;

    return file.replace(domain, proxyPath);
  }, [file]);

  // 监听容器大小变化 (ResizeObserver)
  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.contentRect.width) {
          setContainerWidth(entry.contentRect.width);
        }
      }
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // 初始化加载 PDF.js 库
  useEffect(() => {
    const initPdfJs = async () => {
      if (pdfLibRef.current) return;

      try {
        setLoading(true);
        const pdfjsLib = await import("pdfjs-dist");

        // 设置 Worker
        const baseUrl = import.meta.env.BASE_URL || "/";
        const assetsBaseUrl = `${baseUrl.replace(/\/?$/, "/")}assets/`;
        pdfjsLib.GlobalWorkerOptions.workerSrc = import.meta.env.DEV
          ? new URL(
              "pdfjs-dist/build/pdf.worker.min.mjs",
              import.meta.url,
            ).toString()
          : `${assetsBaseUrl}pdf.worker.min.js`;

        pdfLibRef.current = pdfjsLib;
        setPdfJsReady(true);
      } catch (err) {
        console.error("Failed to load PDF.js library:", err);
        setError(true);
      }
    };

    initPdfJs();
  }, []);

  // 加载 PDF 文件
  useEffect(() => {
    const loadPdf = async () => {
      if (!proxyUrl || !pdfJsReady || !pdfLibRef.current) return;

      setLoading(true);
      setError(false);

      const finalUrl = proxyUrl;

      // 清理旧文档
      if (pdfDocRef.current) {
        try {
          pdfDocRef.current.destroy();
        } catch (e) {
          console.warn("Error destroying previous doc", e);
        }
      }

      try {
        const baseUrl = import.meta.env.BASE_URL || "/";
        const assetsBaseUrl = `${baseUrl.replace(/\/?$/, "/")}assets/`;
        const cMapUrl = `${assetsBaseUrl}cmaps/`;
        const standardFontDataUrl = `${assetsBaseUrl}standard_fonts/`;

        const loadingTask = pdfLibRef.current.getDocument({
          url: finalUrl,
          cMapUrl,
          cMapPacked: true,
          standardFontDataUrl,
          // 禁用 Range 请求，有时能减少 CORS 预检问题
          disableRange: true,
          disableAutoFetch: true,
        });

        const doc = await loadingTask.promise;
        pdfDocRef.current = doc;
        setPdfDoc(doc);
        setTotalPages(doc.numPages);
        setCurrentPage(1); // 重置页码
        setLoading(false);
        onLoadSuccess?.();
      } catch (err) {
        console.error("Error loading PDF file:", err);
        setError(true);
        setLoading(false);
      }
    };

    // 只有当库加载完成且 file 存在时才加载
    if (pdfJsReady && pdfLibRef.current) {
      loadPdf();
    }
  }, [proxyUrl, pdfJsReady]);

  // 渲染页面 (Pagination Mode)
  useEffect(() => {
    if (mode !== "pagination") return;

    let isCancelled = false;

    const renderPage = async () => {
      if (!pdfDoc || !canvasRef.current || !containerRef.current) return;

      try {
        const page = await pdfDoc.getPage(currentPage);

        if (isCancelled) return;

        // 计算缩放比例以适应容器宽度
        const width = containerWidth || containerRef.current.clientWidth;
        const unscaledViewport = page.getViewport({ scale: 1 });
        const newScale = width / unscaledViewport.width;
        // 限制最大缩放
        const finalScale = Math.min(newScale, 2.0);

        const viewport = page.getViewport({ scale: finalScale });
        const canvas = canvasRef.current;
        const context = canvas.getContext("2d");

        if (!context) return;

        // 处理高分屏清晰度
        const dpr = window.devicePixelRatio || 1;
        canvas.width = viewport.width * dpr;
        canvas.height = viewport.height * dpr;
        canvas.style.width = `${viewport.width}px`;
        canvas.style.height = `${viewport.height}px`;

        context.setTransform(1, 0, 0, 1, 0, 0);
        context.scale(dpr, dpr);

        // 取消之前的渲染任务
        if (renderTaskRef.current) {
          try {
            renderTaskRef.current.cancel();
            await renderTaskRef.current.promise;
          } catch {
            // Ignore cancellation errors
          }
        }

        if (isCancelled) return;

        const renderContext = {
          canvasContext: context,
          viewport: viewport,
        };

        const renderTask = page.render(renderContext);
        renderTaskRef.current = renderTask;

        await renderTask.promise;
      } catch (err: any) {
        if (err?.name !== "RenderingCancelledException") {
          console.error("Error rendering page:", err);
        }
      }
    };

    renderPage();

    return () => {
      isCancelled = true;
      if (renderTaskRef.current) {
        try {
          renderTaskRef.current.cancel();
        } catch {
          // ignore
        }
      }
    };
  }, [pdfDoc, currentPage, mode, containerWidth]);

  const renderPaginationMode = () => (
    <>
      <div
        style={{
          margin: "12px 0",
          display: "flex",
          gap: 16,
          alignItems: "center",
        }}
      >
        <Button
          disabled={currentPage <= 1}
          onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
        >
          上一頁
        </Button>
        <span>
          第 {currentPage} 頁 / 共 {totalPages} 頁
        </span>
        <Button
          disabled={currentPage >= totalPages}
          onClick={() =>
            setCurrentPage((prev) => Math.min(prev + 1, totalPages))
          }
        >
          下一頁
        </Button>
      </div>

      <div
        style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.1)", background: "#fff" }}
      >
        <canvas ref={canvasRef} />
      </div>
    </>
  );

  const renderScrollMode = () => (
    <div
      style={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 0,
        alignItems: "center",
      }}
    >
      {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
        <PDFPage
          key={pageNum}
          pageNumber={pageNum}
          pdfDoc={pdfDoc!}
          containerWidth={
            containerWidth || containerRef.current?.clientWidth || 800
          }
        />
      ))}
    </div>
  );

  if (loading) {
    return (
      <div
        style={{
          padding: 40,
          textAlign: "center",
          width: "100%",
          background: "#f5f5f5",
        }}
      >
        <Spin tip="正在載入檔案..." />
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          padding: 40,
          textAlign: "center",
          width: "100%",
          background: "#f5f5f5",
        }}
      >
        <div style={{ color: "red", marginBottom: 16 }}>
          無法預覽檔案（可能是跨域 CORS 限制）
        </div>
        <div style={{ fontSize: 12, color: "#666", marginBottom: 24 }}>
          瀏覽器安全策略阻止了應用讀取該檔案，但您可以直接下載查看。
        </div>
        <Button
          type="primary"
          icon={<DownloadOutlined />}
          href={file}
          target="_blank"
          rel="noopener noreferrer"
        >
          直接打開/下載檔案
        </Button>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      style={{
        width: "100%",
        minHeight: "400px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        backgroundColor: "#f5f5f5",
        padding: 0,
        overflow: mode === "pagination" ? "hidden" : "auto",
        height: mode === "pagination" ? "auto" : "100%",
      }}
    >
      {mode === "pagination" ? renderPaginationMode() : renderScrollMode()}
    </div>
  );
};

// 子组件：用于滚动模式下的单页渲染
const PDFPage = ({
  pageNumber,
  pdfDoc,
  containerWidth,
}: {
  pageNumber: number;
  pdfDoc: PDFDocumentProxy;
  containerWidth: number;
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const renderTaskRef = useRef<any>(null);

  useEffect(() => {
    let isCancelled = false;

    const render = async () => {
      if (!pdfDoc || !canvasRef.current) return;
      try {
        const page = await pdfDoc.getPage(pageNumber);

        if (isCancelled) return;

        const unscaledViewport = page.getViewport({ scale: 1 });
        const scale = containerWidth / unscaledViewport.width;
        const finalScale = Math.min(scale, 2.0);
        const viewport = page.getViewport({ scale: finalScale });

        const canvas = canvasRef.current;
        const context = canvas.getContext("2d");
        if (!context) return;

        const dpr = window.devicePixelRatio || 1;
        canvas.width = viewport.width * dpr;
        canvas.height = viewport.height * dpr;
        canvas.style.width = `${viewport.width}px`;
        canvas.style.height = `${viewport.height}px`;

        context.setTransform(1, 0, 0, 1, 0, 0);
        context.scale(dpr, dpr);

        if (renderTaskRef.current) {
          try {
            renderTaskRef.current.cancel();
            await renderTaskRef.current.promise;
          } catch {
            // Ignore
          }
        }

        if (isCancelled) return;

        const renderTask = page.render({
          canvasContext: context,
          viewport: viewport,
        });
        renderTaskRef.current = renderTask;

        await renderTask.promise;
      } catch (e: any) {
        if (e?.name !== "RenderingCancelledException") {
          console.error(`Error rendering page ${pageNumber}`, e);
        }
      }
    };
    render();

    return () => {
      isCancelled = true;
      if (renderTaskRef.current) {
        try {
          renderTaskRef.current.cancel();
        } catch {
          // ignore
        }
      }
    };
  }, [pageNumber, pdfDoc, containerWidth]);

  return (
    <div
      id={`pdf-page-${pageNumber}`}
      style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.1)", background: "#fff" }}
    >
      <canvas ref={canvasRef} />
    </div>
  );
};
